import { AsyncPipe } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import {
  EditService,
  GridComponent,
  GridModule,
  IEditCell,
  PageService,
  SaveEventArgs,
  ToolbarService
} from '@syncfusion/ej2-angular-grids';
import { Observable } from 'rxjs';
import { TdRateBank, TdRateBankToAddOrUpdate } from './td-rate-bank.model';
import { Query } from '@syncfusion/ej2-data';
import { TdRatesBanksService } from './td-rates-banks.service';
import { ApiErrorDialogComponent } from '../shared/api-error-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ToastComponent } from '../shared/toast.component';
import { SpinnerComponent } from '../shared/spinner.component';
import { CurrencyFootnoteEditorComponent } from '../shared/currency-footnote-editor.component';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'eaa-td-rates-banks',
  imports: [
    AsyncPipe,
    GridModule,
    FormsModule,
    SpinnerComponent,
    CurrencyFootnoteEditorComponent,
    ToastComponent
  ],
  providers: [ToolbarService, EditService, PageService],
  template: `
    <div class="card">
      <div class="card-header">
        <h4 class="card-title m-0">TD RATES - BANKS</h4>
      </div>
      <div class="card-body p-0">
        <ejs-grid
          [dataSource]="banks$ | async"
          [editSettings]="{
            allowEditing: true,
            allowAdding: true,
            allowDeleting: true,
            mode: 'Dialog'
          }"
          [toolbar]="['Add', 'Edit', 'Update', 'Delete', 'Cancel']"
          id="grid"
          #grid
          allowPaging="true"
          allowTextWrap="true"
          [pageSettings]="{ pageSizes: true, pageSize: 20 }"
          (actionBegin)="onActionBegin($event)"
          (actionComplete)="onActionComplete($event)"
        >
          <e-columns>
            <e-column field="id" headerText="#" width="70" [isPrimaryKey]="true" [allowEditing]="false" />
            <e-column field="name" headerText="Name" editType="stringedit" [validationRules]="{ required: true }" />
            <e-column
              field="region"
              headerText="Region"
              width="160"
              editType="dropdownedit"
              [edit]="regionEditParams"
              [validationRules]="{ required: true }"
            />
            <e-column
              field="location"
              headerText="Location"
              editType="stringedit"
              [validationRules]="{ required: true }"
            />
            <e-column field="creditRating" headerText="Credit Rating" editType="stringedit" />
            <e-column field="currencyFootnotes" headerText="Footnotes">
              <ng-template #template let-data>
                <div class="d-flex flex-column gap-1">
                  @for (footnote of data.currencyFootnotes; track $index) {
                    <span
                      ><strong>{{ footnote.currency }}:</strong> {{ footnote.footnote }}
                    </span>
                  }
                </div>
              </ng-template>
              <ng-template #editTemplate let-data>
                <eaa-currency-footnote-editor [(ngModel)]="editedBank!.currencyFootnotes" />
              </ng-template>
            </e-column>
            <e-column field="comments" headerText="Comments" editType="stringedit" />
          </e-columns>
        </ejs-grid>
      </div>
    </div>

    <eaa-toast #toastTdRatesBanksAction [showCloseButton]="false">
      <div id="title">PROCESSING</div>
      <div id="content" class="d-flex gap-2">
        <eaa-spinner [diameter]="20" />
        <div>
          {{ processingMessage }}
        </div>
      </div>
    </eaa-toast>
  `
})
export class TdRatesBanksComponent implements OnInit {
  public banks$: Observable<TdRateBank[]> | null = null;
  public editedBank?: TdRateBankToAddOrUpdate;
  public processingMessage = '';

  public regionEditParams?: IEditCell = {
    params: { dataSource: ['Asia', 'Europe', 'MENA'], query: new Query(), actionComplete: () => false }
  };

  @ViewChild('grid')
  public grid?: GridComponent;

  @ViewChild('toastTdRatesBanksAction')
  public toastTdRatesBanksAction?: ToastComponent;

  constructor(
    private tdRatesBanksService: TdRatesBanksService,
    public apiErrorDialog: MatDialog
  ) {}

  public ngOnInit(): void {
    this.banks$ = this.tdRatesBanksService.getTdRateBanks$();
  }

  public onActionBegin(args: SaveEventArgs) {
    if (args.requestType === 'beginEdit' || args.requestType === 'add') {
      this.editedBank = Object.assign({}, args.rowData) as TdRateBankToAddOrUpdate;
    }
    if (args.requestType === 'save') {
      (args.data as TdRateBankToAddOrUpdate).currencyFootnotes = this.editedBank?.currencyFootnotes || [];
    }
  }

  public onActionComplete = (args: any) => {
    if (args.requestType === 'beginEdit' || args.requestType === 'add') {
      const dialog = (args as any).dialog;
      dialog.showCloseIcon = false;
      dialog.width = 900;
      dialog.header =
        args.requestType === 'beginEdit' ? `Edit bank ${(args.rowData as TdRateBankToAddOrUpdate).name}` : 'New bank';
    } else if (args.requestType === 'save') {
      const bank = args.data as TdRateBank;

      if (bank.id) {
        this.processingMessage = `Updating TD rate bank ${bank.name}...`;
        this.toastTdRatesBanksAction?.show();

        this.tdRatesBanksService.updateTdRateBank$(bank.id, bank).subscribe({
          next: (updated) => this.handleAfterExecute(true, `Updated TD rate bank ${updated.name}`),
          error: (error) => this.handleAfterExecute(false, null, error)
        });
      } else {
        this.processingMessage = `Adding new TD rate bank ${bank.name}...`;
        this.toastTdRatesBanksAction?.show();

        this.tdRatesBanksService.insertTdRateBank$(bank).subscribe({
          next: (inserted) => this.handleAfterExecute(true, `Added new TD rate bank ${inserted.name}`),
          error: (error) => this.handleAfterExecute(false, null, error)
        });
      }
    } else if (args.requestType === 'delete') {
      const banks = (args.data as TdRateBank[]) || [];

      if (banks.length === 0) {
        this.resetToast();
        return;
      }

      this.processingMessage = `Deleting TD rate banks ${banks.map((b) => b.name).join(', ')}...`;
      this.toastTdRatesBanksAction?.show();

      this.tdRatesBanksService.deleteMultipleTdRateBanks$(banks.map((b) => b.id)).subscribe({
        next: () => this.handleAfterExecute(true, `Deleted TD rate banks ${banks.map((b) => b.name).join(', ')}`),
        error: (error) => this.handleAfterExecute(false, null, error)
      });
    }
  };

  private handleAfterExecute(
    success: boolean,
    processingMessage: string | null = null,
    error: any | null = null
  ): void {
    if (success) {
      this.banks$ = this.tdRatesBanksService.getTdRateBanks$();
      this.processingMessage = processingMessage || `Completed.`;

      setTimeout(() => {
        this.resetToast();
      }, 1500);
    } else {
      this.resetToast();

      if (error) {
        this.apiErrorDialog.open(ApiErrorDialogComponent, {
          width: '800px',
          data: error
        });
      }
    }
  }

  private resetToast() {
    this.toastTdRatesBanksAction?.hide();
    this.processingMessage = '';
  }
}
